import { useEffect, useMemo } from 'react';
import { Button, Card, Col, Form, Modal, Row, Stack } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api } from 'src/models/Api.model';
import { DilationEnum } from 'src/models/Dilation.model';
import { PatientInfoKeyMapOptain, Session } from 'src/models/Session.model';

import useGetSessions from 'src/hooks/useGetSessions';
import useUiTranslation from 'src/hooks/useUiTranslation';

interface ResumeInformationModalProps {
  currentSession: Session;
  isShowResumeModal: boolean;
  handleSetIsShowResumeModal: (isShowResumeModal: boolean) => void;
}

export function ResumeInformationModal({
  currentSession,
  isShowResumeModal,
  handleSetIsShowResumeModal,
}: ResumeInformationModalProps) {
  const history = useHistory();

  const { setBackendStatus, setSession } = useRemoteBoundStore();

  const { getSessions } = useGetSessions();

  const { tCommon } = useUiTranslation();
  const patientInfoConfirmation = useMemo(() => {
    const { id, firstName, lastName, dob } = currentSession;
    return { id, firstName, lastName, dob };
  }, [currentSession]);

  const {
    handleSubmit,
    register,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm({
    defaultValues: {
      leftEyeDilated: DilationEnum.NotDilated,
      rightEyeDilated: DilationEnum.NotDilated,
    },
  });

  const getDilationValue = (dilatedTime: Date | undefined) => {
    return dilatedTime ? DilationEnum.Dilated : DilationEnum.NotDilated;
  };

  useEffect(() => {
    reset({
      leftEyeDilated: getDilationValue(currentSession.leftEyeDilatedTime),
      rightEyeDilated: getDilationValue(currentSession.rightEyeDilatedTime),
    });
  }, [currentSession, reset]);

  const getDilatedTime = (dilation: DilationEnum) => {
    return dilation === DilationEnum.Dilated ? new Date() : undefined;
  };

  const onSubmit: SubmitHandler<{
    leftEyeDilated: DilationEnum;
    rightEyeDilated: DilationEnum;
  }> = async (data) => {
    if (!currentSession.sessionId) {
      throw new Error(tCommon('message.error.device-data-id-is-required'));
    }
    const backendState = await Api.remote.get_backend_state(
      currentSession.sessionId,
    );
    setBackendStatus(backendState);

    await getSessions(currentSession.sessionId);

    if (isDirty) {
      const updatedSession = {
        ...currentSession,
        leftEyeDilatedTime: getDilatedTime(data.leftEyeDilated),
        rightEyeDilatedTime: getDilatedTime(data.rightEyeDilated),
      };

      setSession(updatedSession);
    } else {
      setSession(currentSession);
    }

    history.push({
      pathname: '/session-preview',
      state: {
        isResume: true,
        leftImageFileName: currentSession.leftImageFileName,
        rightImageFileName: currentSession.rightImageFileName,
      },
    });
  };

  return (
    <CustomModal
      show={isShowResumeModal}
      onHide={() => handleSetIsShowResumeModal(false)}
      scrollable={true}
      fullscreen="xl-down"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {tCommon('patientFields.confirmPatientInformation')}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Card className="mb-4">
            <Card.Body>
              <Stack gap={2}>
                {(
                  Object.keys(patientInfoConfirmation) as Array<
                    keyof Session & keyof typeof PatientInfoKeyMapOptain
                  >
                ).map((objKey) => (
                  <Row key={objKey}>
                    <Col>
                      <span className="fw-medium">
                        {tCommon(
                          `patientFields.${PatientInfoKeyMapOptain[objKey]}`,
                        )}
                      </span>
                    </Col>
                    <Col>
                      <span>{patientInfoConfirmation[objKey]}</span>
                    </Col>
                  </Row>
                ))}
              </Stack>
            </Card.Body>
          </Card>
          <div className="border rounded p-3 shadow">
            <Form.Group as={Row} className="mb-3">
              <Form.Label column className="fw-medium">
                {tCommon('examFields.leftEyeDilation')}
              </Form.Label>

              <Col>
                <Form.Group className="gap-2">
                  <Form.Check
                    type="radio"
                    label={tCommon('examFields.notDilated')}
                    value="Not Dilated"
                    {...register('leftEyeDilated')}
                  />
                  <Form.Check
                    type="radio"
                    label={tCommon('examFields.dilated')}
                    value="Dilated"
                    {...register('leftEyeDilated')}
                  />
                </Form.Group>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column className="fw-medium">
                {tCommon('examFields.rightEyeDilation')}
              </Form.Label>
              <Col>
                <Form.Group className="gap-2">
                  <Form.Check
                    type="radio"
                    label={tCommon('examFields.notDilated')}
                    value="Not Dilated"
                    {...register('rightEyeDilated')}
                  />
                  <Form.Check
                    type="radio"
                    label={tCommon('examFields.dilated')}
                    value="Dilated"
                    {...register('rightEyeDilated')}
                  />
                </Form.Group>
              </Col>
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary-100"
            onClick={() => handleSetIsShowResumeModal(false)}
          >
            {tCommon('modal.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? tCommon('modal.confirming')
              : tCommon('modal.confirm')}
          </Button>
        </Modal.Footer>
      </Form>
    </CustomModal>
  );
}
